import { useEffect, useState, useCallback, useMemo } from 'react';
import type { ReactElement } from 'react';

import { ApiClient, ApiError, PaginatedResponse } from '@/api';
import { Message, StampAction } from '@/entities';

import { useCSPRClick } from './use-cspr-click';

export type PaginatedApiRequestState<T> = {
  loading: boolean;
  response: PaginatedResponse<T> | null;
  error: {
    code: string;
    message: string;
    description?: string | ReactElement;
  } | null;
};

export const useApi = () => {
  const { clickRef, csprClickReady } = useCSPRClick();

  const apiClient = useMemo(
    () => (clickRef ? new ApiClient(clickRef) : null),
    [clickRef?.digestAuth]
  );

  return { apiClient, apiReady: csprClickReady };
};

const useMakePaginatedRequest = <T, A extends readonly unknown[]>(
  methodSelector: (client: ApiClient) => (...args: A) => Promise<PaginatedResponse<T>>,
  ...args: A
): PaginatedApiRequestState<T> => {
  const [state, setState] = useState<PaginatedApiRequestState<T>>({
    loading: false,
    response: null,
    error: null
  });

  const { apiClient, apiReady } = useApi();

  useEffect(() => {
    if (!apiClient) {
      return;
    }

    let canceled = false;

    (async () => {
      setState((prev) => ({
        loading: true,
        response: prev.response,
        error: null
      }));

      try {
        const response = await methodSelector(apiClient).bind(apiClient)(...args);
        if (canceled) {
          return;
        }

        setState({
          response,
          loading: false,
          error: null,
        });
      } catch (err) {
        if (canceled) {
          return;
        }

        console.log(err);
        setState({
          loading: false,
          response: null,
          error: {
            message: err instanceof Error ? err.message : String(err),
            code: err instanceof ApiError ? err.code : '500',
            description: err instanceof ApiError ? err.description : '500',
          }
        });
      }
    })();

    return () => {
      canceled = true;
    };
  }, [apiClient, methodSelector, ...args]);

  return state;
}

export const useGetAllMessages = (
  page: number = 1,
  perPage: number = 10
): PaginatedApiRequestState<Message> => {
  const selectGetAllMessages = useCallback(
    (client: ApiClient) => client.getAllMessages,
    []
  );

  return useMakePaginatedRequest(selectGetAllMessages, page, perPage);
};

export const useMessagesReceivedByAccount = (
  publicKey: string,
  page: number = 1,
  perPage: number = 10
): PaginatedApiRequestState<Message> => {
  const selectGetMessagesReceivedBy = useCallback(
    (client: ApiClient) => client.getMessagesReceivedBy,
    []
  );

  return useMakePaginatedRequest(selectGetMessagesReceivedBy, publicKey, page, perPage);
};

export const useMessagesSentByAccount = (
  publicKey: string,
  page: number = 1,
  perPage: number = 10
): PaginatedApiRequestState<Message> => {
  const selectGetMessagesSentBy = useCallback(
    (client: ApiClient) => client.getMessagesSentBy,
    []
  );

  return useMakePaginatedRequest(selectGetMessagesSentBy, publicKey, page, perPage);
};

export const useStampsHistoryForAccount = (
  publicKey: string,
  page: number = 1,
  perPage: number = 10
): PaginatedApiRequestState<StampAction> => {
  const selectGetStampsHistoryFor = useCallback(
    (client: ApiClient) => client.getStampsHistoryFor,
    []
  );

  return useMakePaginatedRequest(selectGetStampsHistoryFor, publicKey, page, perPage);
};
